import { useCallback } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { tokens, Icon, Tooltip } from '@hotjar/hotsauce';

import type { IconName } from '../domains/model';
import { DEFAULT_LABEL_ICONS } from '../constants/label-icons';

const Wrapper = styled.span`
  display: inline-block;
  position: relative;
`;

// the labels used to be 10px smaller than the L size for icons so we need to scale down accordingly
const ScaledIcon = styled(Icon)<{ labelSize: number }>`
  transform: scale(${({ labelSize }) => (labelSize - 10) / labelSize});
`;

const Checkbox = styled.input`
  // NOTE: display none is not the best idea if you want to keep it accessible
  // @source https://webaim.org/techniques/css/invisiblecontent/
  display: block;
  height: 0;
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  width: 0;
`;

const CheckboxLabel = styled.label<{ isSelected: boolean; size: number }>`
  align-items: center;
  display: inline-flex;
  height: ${({ size }) => size}px;
  justify-content: center;
  margin: 0;
  width: ${({ size }) => size}px;

  ${({ isSelected }) => (isSelected ? tokens.elevation(1) : null)};
  background-color: ${({ isSelected }) => (isSelected ? tokens.color.white : 'transparent')};
  &:hover {
    background-color: ${({ isSelected }) =>
      isSelected ? tokens.color.white : tokens.color.neutral_100};
  }
  ${tokens.transition('background-color')};

  border-radius: ${tokens.radius.m};
  cursor: pointer;

  &:focus-visible {
    ${tokens.focus()};
  }
`;

interface LabelProps {
  iconName: IconName;
  id: string;
  isSelected: boolean;
  name: string;
  onAdd: () => void;
  onRemove: () => void;
  size: number;
  keyName: string | null;
}

export function LabelButton({
  iconName,
  id,
  isSelected,
  name,
  onAdd,
  onRemove,
  size,
  keyName,
}: LabelProps): JSX.Element {
  const checkboxId = `highlight-label-${id}`;
  const { t } = useTranslation();

  const handleChange = useCallback(
    ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
      if (isSelected && !checked) {
        onRemove();
        return;
      }

      if (!isSelected && checked) {
        onAdd();
      }
    },
    [isSelected, onAdd, onRemove],
  );

  return (
    <Wrapper>
      <Checkbox checked={isSelected} id={checkboxId} onChange={handleChange} type="checkbox" />
      <Tooltip text={keyName !== null ? t(keyName) : name}>
        <CheckboxLabel htmlFor={checkboxId} isSelected={isSelected} size={size}>
          <ScaledIcon icon={DEFAULT_LABEL_ICONS[iconName] ?? 'unknown'} size="l" labelSize={size} />
        </CheckboxLabel>
      </Tooltip>
    </Wrapper>
  );
}
